import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryPolicies } from '../../../utils/queryPolicies';
import { fetchMerchantFieldFills, fetchMerchantLeads, type MerchantLead } from '../../LeadImport/api';
import { leadImportKeys } from '../../LeadImport/keys';
import LeadDrawer from '../../LeadImport/LeadDrawer';
import FieldFills from '../../LeadImport/FieldFills';
import { fmtDay } from '../../LeadImport/ui';

/**
 * Leads tab: every imported lead linked to this merchant or sharing one of its
 * phones, newest first. Operator-only (lead_import capability); the RPC checks
 * the capability and merchant access again. A card opens the lead; the file
 * name opens its import. Above them, values leads gave this merchant: fields
 * they filled and different values that were kept aside.
 */
function MerchantLeadsTab({ merchantId }: { merchantId: number }) {
  const q = useQuery({
    queryKey: leadImportKeys.merchantLeads(merchantId),
    queryFn: () => fetchMerchantLeads(merchantId),
    ...queryPolicies.recordDetail,
  });
  const fills = useQuery({
    queryKey: leadImportKeys.merchantFills(merchantId),
    queryFn: () => fetchMerchantFieldFills(merchantId),
    ...queryPolicies.recordDetail,
  });
  const [lead, setLead] = useState<number | null>(null);

  if (q.isLoading) return <div className="sky-mp-empty">Loading...</div>;
  if (q.error) return <div className="sky-mp-empty">{(q.error as Error).message}</div>;

  const rows = q.data ?? [];
  const imports = new Set(rows.map((r) => r.batch)).size;

  return (
    <div className="sky-mp-lead-tab">
      {(fills.data ?? []).length > 0 && (
        <div className="sky-mp-summary" data-testid="merchant-lead-values">
          <div className="sky-mp-summary-head">
            <span className="sky-mp-summary-label">VALUES FROM LEADS</span>
          </div>
          <div className="sky-mp-lead-fills">
            <div className="sky-mp-empty-inline">
              {fills.data!.some((f) => f.outcome === 'conflict')
                ? 'Leads gave different values for fields this merchant already has. They were not applied.'
                : 'Fields this merchant had empty, filled from its leads.'}
            </div>
            <FieldFills fills={fills.data!} onOpenLead={setLead} />
          </div>
        </div>
      )}
      <div className="sky-mp-summary">
        <div className="sky-mp-summary-head">
          <span className="sky-mp-summary-label">IMPORTED LEADS</span>
          {rows.length > 0 && (
            <span className="sky-mp-lead-count">
              {rows.length} {rows.length === 1 ? 'lead' : 'leads'} from {imports} {imports === 1 ? 'import' : 'imports'}
            </span>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="sky-mp-empty-inline">No imported lead has this merchant&apos;s phone numbers.</div>
        ) : (
          <div className="sky-mp-sub-list" style={{ marginTop: 10 }}>
            {rows.map((r) => <LeadCard key={r.lead} lead={r} onOpen={() => setLead(r.lead)} />)}
          </div>
        )}
      </div>
      <LeadDrawer leadId={lead} onClose={() => setLead(null)} onOpenLead={setLead} />
    </div>
  );
}

function LeadCard({ lead: r, onOpen }: { lead: MerchantLead; onOpen: () => void }) {
  const contact = [r.first_name, r.last_name].filter(Boolean).join(' ');
  const details = [r.business_name, contact, r.phone].filter(Boolean) as string[];
  return (
    <div
      className="sky-mp-sub-card"
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
    >
      <div className="sky-mp-sub-card-top">
        <div className="sky-mp-sub-card-info">
          <span className="sky-mp-sub-date">{fmtDay(r.batch_date)}</span>
          <span className="sky-mp-sub-lender">{r.source ?? 'Unknown source'}{r.sub_source ? ` ${r.sub_source}` : ''}</span>
          <span className="sky-mp-sub-id">#{r.lead}</span>
        </div>
        <span className="sky-mp-sub-pills">
          {r.relation === 'same_phone' && (
            <span className="sky-mp-lead-tag" title="Not linked to this merchant, but its phone is one of the merchant's phones">Same phone</span>
          )}
          <span className={`sky-mp-lead-tag ${r.rep_name ? 'sky-mp-lead-tag--rep' : ''}`}>{r.rep_name ?? (r.ever_assigned ? 'Unassigned now' : 'Never assigned')}</span>
        </span>
      </div>
      {details.length > 0 && (
        <div className="sky-mp-sub-details">
          {details.map((d, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="sky-mp-sub-dot">&bull;</span>}
              <span>{d}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="sky-mp-lead-file">
        <Link to={`/Imports?batch=${r.batch}`} onClick={(e) => e.stopPropagation()}>{r.file_name}</Link>
      </div>
    </div>
  );
}

export default MerchantLeadsTab;
